import React, { useState, useEffect } from 'react';
import {
  Card, Space, Button, Tag, Descriptions, Statistic, Row, Col, 
  message, Typography, Divider, Popconfirm, Alert
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  CloseCircleOutlined, DisconnectOutlined, LinkOutlined,
  ThunderboltOutlined, PoweroffOutlined, DatabaseOutlined
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import { ibuildProxyService } from '../services/ibuildProxyService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface MQHealth {
  healthy: boolean;
  message?: string;
}

interface MQStatus {
  isConnected: boolean;
  hasConnection: boolean;
  hasChannel: boolean;
  hasReconnectScheduled: boolean;
  queue?: string;
  mqUrl?: string;
  consumerTag?: string;
  lastError?: string;
  lastConnectedAt?: string;
  reconnectAttempts?: number;
}

interface MQQueueStats {
  queue: string;
  messageCount: number;
  consumerCount: number;
  available: boolean;
  error?: string;
}

const IBuildProxyMQManagement: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<MQHealth | null>(null);
  const [status, setStatus] = useState<MQStatus | null>(null);
  const [queueStats, setQueueStats] = useState<MQQueueStats | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  useEffect(() => {
    loadMQStatus();
    // 每30秒自动刷新一次
    const interval = setInterval(() => {
      loadMQStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadMQStatus = async () => {
    try {
      setLoading(true);
      const [healthResult, statusResult, statsResult] = await Promise.all([
        ibuildProxyService.getMQHealth(),
        ibuildProxyService.getMQStatus(),
        ibuildProxyService.getMQQueueStats().catch(err => {
          console.warn('获取队列统计信息失败:', err);
          return { success: false, error: err.message };
        })
      ]);

      console.log('MQ Health Result:', healthResult);
      console.log('MQ Status Result:', statusResult);
      console.log('MQ Queue Stats Result:', statsResult);

      // 优先使用 statusResult，因为它通常包含更完整的状态信息
      if (statusResult.success) {
        setHealth(statusResult.health || healthResult.health);
        setStatus(statusResult.status);
        setLastUpdate(dayjs().format('YYYY-MM-DD HH:mm:ss'));
      } else if (healthResult.success) {
        // 如果 statusResult 失败，至少使用 healthResult
        setHealth(healthResult.health);
        setStatus(healthResult.status);
        setLastUpdate(dayjs().format('YYYY-MM-DD HH:mm:ss'));
      } else {
        const errorMsg = healthResult.error || healthResult.message || statusResult.error || statusResult.message || '未知错误';
        message.error(`加载 MQ 状态失败: ${errorMsg}`);
        console.error('MQ 状态加载失败:', { healthResult, statusResult });
      }

      // 设置队列统计信息
      if (statsResult.success && statsResult.data) {
        setQueueStats(statsResult.data);
      } else {
        setQueueStats(null);
      }
    } catch (error: any) {
      message.error(`加载 MQ 状态失败: ${error.message}`);
      console.error('MQ 状态加载失败:', error);
      // 清空状态，避免显示错误数据
      setHealth(null);
      setStatus(null);
      setQueueStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = async () => {
    try {
      setLoading(true);
      const result = await ibuildProxyService.reconnectMQ();
      
      if (result.success) {
        message.success(result.message || 'MQ 重新连接成功');
        // 重新加载状态
        await loadMQStatus();
      } else {
        message.error(result.error || result.message || 'MQ 重新连接失败');
      }
    } catch (error: any) {
      message.error(`重新连接 MQ 失败: ${error.message}`);
      console.error('MQ 重新连接失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    try {
      setLoading(true);
      const result = await ibuildProxyService.closeMQ();
      
      if (result.success) {
        message.success(result.message || 'MQ 连接已关闭');
        // 重新加载状态
        await loadMQStatus();
      } else {
        message.error(result.error || result.message || '关闭 MQ 连接失败');
      }
    } catch (error: any) {
      message.error(`关闭 MQ 连接失败: ${error.message}`);
      console.error('关闭 MQ 连接失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (connected: boolean) => {
    return connected ? 'success' : 'error';
  };

  const getStatusIcon = (connected: boolean) => {
    return connected ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <Space>
            <LinkOutlined />
            <span>IBuild Proxy MQ 管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMQStatus}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 状态概览 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="连接状态"
                value={status?.isConnected ? '已连接' : '未连接'}
                prefix={getStatusIcon(status?.isConnected || false)}
                valueStyle={{ 
                  color: status?.isConnected ? '#3f8600' : '#cf1322',
                  fontSize: '24px'
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="健康状态"
                value={health?.healthy ? '健康' : '不健康'}
                prefix={health?.healthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                valueStyle={{ 
                  color: health?.healthy ? '#3f8600' : '#cf1322',
                  fontSize: '24px'
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="队列消息数"
                value={queueStats?.messageCount !== undefined && queueStats.messageCount >= 0 ? queueStats.messageCount : 'N/A'}
                prefix={<DatabaseOutlined />}
                valueStyle={{ 
                  color: queueStats?.messageCount && queueStats.messageCount > 0 ? '#faad14' : '#1890ff',
                  fontSize: '24px'
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="消费者数量"
                value={queueStats?.consumerCount !== undefined && queueStats.consumerCount >= 0 ? queueStats.consumerCount : 'N/A'}
                prefix={<LinkOutlined />}
                valueStyle={{ 
                  color: queueStats?.consumerCount && queueStats.consumerCount > 0 ? '#3f8600' : '#cf1322',
                  fontSize: '24px'
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* 队列统计信息 */}
        {queueStats && (
          <Card title="队列数据统计" size="small" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="队列名称"
                  value={queueStats.queue}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="待处理消息数"
                  value={queueStats.available ? queueStats.messageCount : '不可用'}
                  valueStyle={{ 
                    fontSize: '20px',
                    color: queueStats.messageCount > 0 ? '#faad14' : '#3f8600'
                  }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="活跃消费者数"
                  value={queueStats.available ? queueStats.consumerCount : '不可用'}
                  valueStyle={{ 
                    fontSize: '20px',
                    color: queueStats.consumerCount > 0 ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
            </Row>
            {queueStats.error && (
              <Alert
                message="获取队列统计信息失败"
                description={queueStats.error}
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        )}

        {/* 状态提示 */}
        {status && !status.isConnected && (
          <Alert
            message="MQ 连接未建立"
            description={health?.message || status.lastError || 'RabbitMQ 连接未建立，请检查配置或尝试重新连接'}
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 24 }}
            action={
              <Button size="small" onClick={handleReconnect} loading={loading}>
                重新连接
              </Button>
            }
          />
        )}

        {status && status.isConnected && health && health.healthy && (
          <Alert
            message="MQ 连接正常"
            description={health.message || 'RabbitMQ 连接已建立，服务正常运行'}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 详细状态信息 */}
        <Card title="详细状态信息" size="small" style={{ marginBottom: 24 }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="连接状态">
              <Tag color={getStatusColor(status?.isConnected || false)}>
                {status?.isConnected ? '已连接' : '未连接'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="健康状态">
              <Tag color={health?.healthy ? 'success' : 'error'}>
                {health?.healthy ? (health.message || '健康') : '不健康'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="通道状态">
              <Tag color={status?.hasChannel ? 'success' : 'default'}>
                {status?.hasChannel ? '已打开' : '未打开'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="连接对象">
              <Tag color={status?.hasConnection ? 'success' : 'default'}>
                {status?.hasConnection ? '存在' : '不存在'}
              </Tag>
            </Descriptions.Item>
            {status?.mqUrl && (
              <Descriptions.Item label="MQ 地址">
                <Text code copyable>{status.mqUrl}</Text>
              </Descriptions.Item>
            )}
            {status?.queue && (
              <Descriptions.Item label="队列/Exchange">
                <Text code>{status.queue}</Text>
              </Descriptions.Item>
            )}
            {queueStats && queueStats.available && (
              <Descriptions.Item label="队列消息数">
                <Tag color={queueStats.messageCount > 0 ? 'warning' : 'success'}>
                  {queueStats.messageCount}
                </Tag>
              </Descriptions.Item>
            )}
            {queueStats && queueStats.available && (
              <Descriptions.Item label="消费者数量">
                <Tag color={queueStats.consumerCount > 0 ? 'success' : 'error'}>
                  {queueStats.consumerCount}
                </Tag>
              </Descriptions.Item>
            )}
            {status?.hasReconnectScheduled !== undefined && (
              <Descriptions.Item label="重连调度中">
                <Tag color={status.hasReconnectScheduled ? 'warning' : 'default'}>
                  {status.hasReconnectScheduled ? '是' : '否'}
                </Tag>
              </Descriptions.Item>
            )}
            {status?.consumerTag && (
              <Descriptions.Item label="消费者标签">
                <Text code>{status.consumerTag}</Text>
              </Descriptions.Item>
            )}
            {status?.lastConnectedAt && (
              <Descriptions.Item label="最后连接时间">
                {dayjs(status.lastConnectedAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
            {status?.reconnectAttempts !== undefined && (
              <Descriptions.Item label="重连次数">
                {status.reconnectAttempts}
              </Descriptions.Item>
            )}
            {status?.lastError && (
              <Descriptions.Item label="最后错误" span={2}>
                <Text type="danger">{status.lastError}</Text>
              </Descriptions.Item>
            )}
            {lastUpdate && (
              <Descriptions.Item label="最后更新时间" span={2}>
                {lastUpdate}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        <Divider />

        {/* 操作按钮 */}
        <Card title="操作" size="small">
          <Space size="large">
            <Popconfirm
              title="确定要重新连接 MQ 吗？"
              description="这将断开当前连接并尝试重新建立连接"
              onConfirm={handleReconnect}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={loading}
              >
                重新连接 MQ
              </Button>
            </Popconfirm>

            <Popconfirm
              title="确定要关闭 MQ 连接吗？"
              description="关闭连接后，MQ 消息处理将停止，直到重新连接"
              onConfirm={handleClose}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<PoweroffOutlined />}
                loading={loading}
              >
                关闭 MQ 连接
              </Button>
            </Popconfirm>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

export default IBuildProxyMQManagement;

